import React, { useContext, useEffect, useState } from "react";

import Head from "next/head";
import axios from "axios";
import { Modal } from "react-responsive-modal";
import Cookie from "js-cookie";

import About from "components/About";
import Breadcrumbs from "components/Breadcrumbs";
// import EventsLimited from "components/EventsLimited";
import EventsWithContext from "components/EventsWithContext";
import Header from "@components/header";
import Footer from "@components/footer";
import Information from "components/Information";
import Intro from "components/Intro";
import People from "components/People";
import Recommendations from "components/Recommendations";
import SourceFeatured from "components/SourceFeatured";
import SubNavCase from "components/SubNavCase";
import Uncovered from "@components/Uncovered";
import Button from "@components/Button";
import Sources from "components/Sources";
import ContributePanel from "@components/contributeCase/ContributePanel";
import ModalFollowCase from "components/ModalFollowCase";

import { OutsetaContext } from "pages/_app";
import { urlFor } from "lib/sanity";
import MediaGallery from "./MediaGallery";
import { useUser } from "@auth0/nextjs-auth0/client";

import WeNeedHelp from "components/WeNeedHelp";

export default function CasePage({
  caseData,
  // events,
  orderedEventsRecords,
  featuredSource,
  images,
  locations,
  personsList,
  personVictim,
  slug,
  sources,
  uncovereds,
  contentQuery,
  sourceOnlineResources,
  sourcePodcasts,
  sourceMiscRecords,
}) {
  // Only the victim, no events, and no Uncovered prompts for action
  const needsExtraAttention =
    personsList.length < 2 &&
    uncovereds.length < 1 &&
    orderedEventsRecords.length < 1;
  const [customRecommendations, setCustomRecommendations] = useState([]);
  const [contributePanelVisible, setContributePanelVisible] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [showWeNeedHelp, setShowWeNeedHelp] = useState(false);

  const { narrative, primaryImage, socialImage, gallery } =
    contentQuery.data || {};

  const sanityPrimaryImageUrl = primaryImage?.image
    ? urlFor(primaryImage?.image).auto("format").url()
    : undefined;
  const sanitySocialImageUrl = socialImage?.image
    ? urlFor(socialImage?.image).auto("format").url()
    : undefined;

  const {
    age_at_death,
    age_missing,
    classification,
    city,
    contacts,
    date_found,
    date_missing,
    date_of_death,
    date_of_birth,
    estimated_date_of_birth,
    description_text,
    last_updated_date,
    first_name,
    last_name,
    meta_description,
    namus_number,
    necmec_number,
    seo_name,
    state,
    summary_text,
    region,
  } = caseData;

  // Define the victim data to avoid many props
  const victim = {
    ageAtDeath: age_at_death,
    ageAtMissing: age_missing,
    city,
    classification,
    contacts,
    dateFound: date_found,
    dateLastUpdated: last_updated_date,
    dateOfBirth: date_of_birth,
    estimatedDateOfBirth: estimated_date_of_birth,
    dateMissing: date_missing,
    dateOfDeath: date_of_death,
    nameFirst: first_name,
    nameLast: last_name,
    namusNumber: namus_number,
    necmecNumber: necmec_number,
    needsExtraAttention,
    personVictim,
    primaryImageUrl:
      sanityPrimaryImageUrl || (images.length > 0 && images[0].url),
    shortDescription:
      summary_text ||
      (description_text && description_text.length > 256
        ? `${description_text.substring(0, 256)}...`
        : description_text),
    state,
  };

  const { outsetaUserId, addOutsetaUserCaseList, outsetaUserCaseList } =
    useContext(OutsetaContext);

  const { user } = useUser();

  useEffect(() => {
    // Retrieve viewed slugs and add this slug to the array
    const localViewedSlugs = JSON.parse(
      localStorage.getItem("viewedSlugs") || "[]"
    );
    // If the slug isn't in the array, add it
    if (!localViewedSlugs.includes(slug)) {
      localViewedSlugs.push(slug);
    }
    // Set the updated array to localStorage
    localStorage.setItem("viewedSlugs", JSON.stringify(localViewedSlugs));

    // Make a request to the server with the excluded slugs
    async function getCustomRecommendations() {
      try {
        const response = await axios.get(
          `${
            process.env.NEXT_PUBLIC_API_BASE_URL
          }/case/${slug}/recommendation?exclude_slugs=${localViewedSlugs.join(
            "%2C"
          )}`
        );
        // Update state with the custom request
        setCustomRecommendations(response.data.results);
      } catch (error) {
        console.error(error);
      }
    }
    getCustomRecommendations();

    if (
      window.addthis &&
      window.addthis.layers &&
      window.addthis.layers.refresh
    ) {
      window.addthis.layers.refresh();
    }
    const cookieWeNeedHelp = Cookie.get("unc_we_need_help");
    if (!cookieWeNeedHelp) {
      setShowWeNeedHelp(true);
    }
  }, [slug]);

  const hasSeoName = seo_name !== null && seo_name !== "";
  const victimName = hasSeoName ? seo_name : `${first_name} ${last_name}`;

  const metaDescription = meta_description ? meta_description : summary_text;
  const metaTitle = `${classification && classification}: ${victimName} | ${
    city && city
  }, ${city && state} | Uncovered`;

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta content={metaTitle} key="twitter:title" name="twitter:title" />
        <meta content={metaTitle} key="og:title" property="og:title" />
        <meta content={metaTitle} itemProp="name" key="name" />
        <meta
          name="format-detection"
          content="telephone=no, date=no, email=no, address=no"
        />

        {metaDescription && (
          <>
            <meta
              key="twitter:description"
              name="twitter:description"
              content={metaDescription}
            />
            ,
            <meta
              content={metaDescription}
              key="og:description"
              property="og:description"
            />
            ,
            <meta
              name="description"
              content={metaDescription}
              key="item_prop:description"
            />
            ,
            <meta name="twitter:image:alt" content={`${victimName}`} />
            ,
            <meta name="twitter:card" content="summary_large_image" />,
          </>
        )}
        <meta
          content={`https://uncovered.com/cases/${slug}`}
          property="og:url"
        />
        <meta property="og:type" content="website" />
        {images.length > 0 && (
          <meta
            property="og:image"
            content={sanitySocialImageUrl || images[0].url}
          />
        )}
        <link rel="canonical" href={`https://uncovered.com/cases/${slug}`} />
      </Head>
      <Header />
      <main className="bg-gray-100">
        <header
          className={`flex flex-col bg-black text-white lg:after:h-[155px] relative after:content-[''] after:bg-gray-100 after:h-[210px] after:absolute after:bottom-0 after:left-0 after:right-0`}
        >
          <div className="max-w-6xl mx-auto pt-6 px-4 sm:text-center xl:px-0 w-full">
            <Breadcrumbs
              region={region}
              caseData={{ name: victimName, slug }}
            />
          </div>
          <Intro
            addOutsetaUserCaseList={addOutsetaUserCaseList}
            outsetaUserId={outsetaUserId}
            outsetaUserCaseList={outsetaUserCaseList}
            slug={slug}
            victim={victim}
            victimName={victimName}
            contributePanelVisible={contributePanelVisible}
            setContributePanelVisible={setContributePanelVisible}
            signupOpen={signupOpen}
            setSignupOpen={setSignupOpen}
          />
          <Modal
            classNames={{
              modal: "custom-signup-modal",
            }}
            open={signupOpen}
            onClose={() => setSignupOpen(false)}
            center
          >
            <ModalFollowCase
              onCloseModalSignup={() => setSignupOpen(false)}
              slug={slug}
              source="unauthenticated case action"
            />
          </Modal>
        </header>

        {showWeNeedHelp && (
          <WeNeedHelp className="pb-4" setShowWeNeedHelp={setShowWeNeedHelp} />
        )}

        <SubNavCase
          active="overview"
          hasAbout={description_text && description_text.length > 430}
          hasSources={sources && sources.length > 0}
          hasTimeline={orderedEventsRecords && orderedEventsRecords.length > 0}
          slug={slug}
        />

        <section className="max-w-6xl	mx-auto pt-8 px-4 md:flex xl:px-0">
          {(description_text || narrative) && (
            <About
              descriptionHtml={description_text}
              narrative={narrative}
              first_name={first_name}
              last_name={last_name}
            />
          )}
        </section>

        <section className="max-w-6xl	mx-auto pt-8 px-4 xl:px-0">
          <Information
            victim={victim}
            contributePanelVisible={contributePanelVisible}
            setContributePanelVisible={setContributePanelVisible}
            signupOpen={signupOpen}
            setSignupOpen={setSignupOpen}
          />
        </section>

        {gallery?.length && (
          <section className="max-w-6xl	mx-auto pt-8 px-4 xl:px-0">
            <MediaGallery media={gallery} />
          </section>
        )}

        {uncovereds?.length !== undefined && uncovereds.length > 0 && (
          <Uncovered uncovereds={uncovereds} />
        )}

        {featuredSource && (
          <SourceFeatured
            className="flex-shrink-0 md:w-2/6"
            featuredSource={featuredSource}
            slug={slug}
          />
        )}

        {personsList?.length !== undefined && personsList.length > 1 && (
          <section className="max-w-6xl	mx-auto px-4 xl:px-0">
            <People casePeopleRecords={personsList} allImages={images} />
          </section>
        )}

        {orderedEventsRecords?.length !== undefined &&
          orderedEventsRecords.length > 0 && (
            <>
              <div className="content_hint"></div>
              <a id="timeline"></a>
              <section className="max-w-6xl	mx-auto px-4 xl:px-0">
                {/* <EventsLimited
                caseLocationRecords={locations}
                caseSignificantEventRecords={events}
                slug={slug}
              /> */}
                <EventsWithContext
                  caseLocationRecords={locations}
                  caseSignificantEventRecords={orderedEventsRecords}
                  casePeopleRecords={personsList}
                  caseSourcesRecords={sources}
                />
              </section>
            </>
          )}

        <div className="content_hint"></div>
        <a id="sources"></a>
        <section className="max-w-6xl mx-auto pt-2 px-4 xl:px-0">
          <header className="flex items-center justify-between pb-8 pt-16">
            <h1 className="highlight mb-2">Sources</h1>
            {!user ? (
              <Button
                onClick={() => setSignupOpen(true)}
                className="bg-yellow-400 rounded-md mt-7 font-bold text-white py-2 px-5 text-sm top-0 right-0"
              >
                Submit a Source
              </Button>
            ) : (
              <Button
                className="bg-yellow-400 rounded-md mt-7 font-bold text-white py-2 px-3 text-sm top-0 right-0"
                type="button"
                onClick={() =>
                  setContributePanelVisible(!contributePanelVisible)
                }
              >
                Submit a Source
              </Button>
            )}
          </header>

          {sourceOnlineResources?.length !== undefined &&
            sourceOnlineResources.length > 0 && (
              <>
                <h2 className="mb-6 text-xl">
                  Online Resources ({sourceOnlineResources.length})
                </h2>
                <Sources caseSourcesRecords={sourceOnlineResources} />
              </>
            )}

          {sourcePodcasts?.length !== undefined &&
            sourcePodcasts.length > 0 && (
              <>
                <h2 className="mb-6 text-xl">
                  Podcasts ({sourcePodcasts.length})
                </h2>
                <Sources caseSourcesRecords={sourcePodcasts} />
              </>
            )}

          {sourceMiscRecords?.length !== undefined &&
            sourceMiscRecords.length > 0 && (
              <>
                <h2 className="mb-6 text-xl">
                  Misc ({sourceMiscRecords.length})
                </h2>
                <Sources caseSourcesRecords={sourceMiscRecords} />
              </>
            )}
        </section>
        <Recommendations recommendations={customRecommendations} />
        {contributePanelVisible && (
          <ContributePanel
            panelVisible={contributePanelVisible}
            setPanelVisible={setContributePanelVisible}
            outsetaUserId={outsetaUserId}
            slug={slug}
            source="case intro"
          />
        )}
      </main>
      <Footer />
    </>
  );
}
